// The Compensation worksheet — `#compensation/RQ-####` (G §5 "Compensation: Pending -> In Review -> Finalized.";
// "Compensation alone finalizes official recommendation/range."). The review is keyed on the Hiring Request; the
// requisition, once created, takes the finalized range. Compensation reviews (Pending → In Review, `comp_review.start`),
// saves the figures of his task as draft versions (`comp_review.save`) and finalizes (`comp_review.finalize`, which
// refuses until the recommendation and the recommended range are set); every save is a version, the earlier ones kept.
// Every other seat reads what the field policy sends it — the Hiring Manager the recommendation and the recommended
// range once Finalized, nothing of the grade, market data, internal range or working notes: a row whose key is absent
// is not drawn ("Restricted fields must also be absent from search, exports, reports, email, attachments, audit
// summaries and APIs."). The Recruiter or the HR Business Partner may *Return* a finalized review with a reason
// (`comp_review.request_change`). Writes send the review's row_version; a stale one refuses and the card offers Retry.
// Finalize checks the worksheet first: a recommendation or recommended range left empty reads Required on its field and
// nothing is sent, and a refusal that names a field is shown there, never as the card's banner (UAT F-26).
// The header names the job the review prices: the request's title, location, department, Hiring Manager and proposed
// range, the range only when the field policy sent it (UAT F-27).
// Gap (S09 report): `comp_review.fetch_market_data` has no verb of his yet, so the worksheet does not offer it.
// Hosted by MissionShell: views/b2/register.ts registers it through S12's registerView, so it draws no McRoot of its own.

import { useEffect, useId, useState, type ReactNode } from "react";
import {
  compReviewFinalize, compReviewOpen, compReviewRequestChange, compReviewSave, compReviewStart, type CompReviewSaveInput, type GradeOption,
  type VersionOut,
} from "@/api/tools";
import { inputMaxLength } from "@/api/inputLimits";
import { isClientRefusal, useWrite } from "@/api/useTool";
import { useSession } from "@/session/useSession";
import { useViewState } from "@/shell/viewState";
import { NotFound } from "@/views/NotFound";
import {
  ACTION_LABELS, Banner, Button, Card, COMMON_LABELS, COMPENSATION_REVIEW_STATUS, DEFAULT_TIME_ZONE, DIALOG_LABELS, Dialog, Divider, ErrorStrip,
  Field, Flex, G1_LABELS, Grid, Input, KeyValue, LEVEL_DISPLAY, NAV_ITEM_LABELS, PageHeader, Paragraph, Pill, ROLE_LABELS, RULE_LABELS, Rail,
  Segmented, Select, Skeleton, Split, Stack, REQUEST_STATUS, TextArea, TextLink, Toggle, Versions, type KeyValueItem, type Level, type RailState,
} from "@/mc";
import { useSettledRead } from "@/views/shared/settledRead";
import { range } from "../requisition/data";
import { COMP_ROW_LABELS, REQ_LABELS, REQ_RULES } from "../requisition/labels";
import { atMostCharacters, betweenBounds, COMP_ACTION_LABELS, COMP_FIELD_LABELS, COMP_RULES, HIRING_RANGE_LABELS, internalRangeLine, lengthCounter } from "./labels";
import { TIMING_LABELS } from "../request/labels";
import { MarketData, readSections } from "./rows";

const READS = (requestId: string) => [["comp-review", requestId], ["comp-review-queue"], ["requisition"], ["today"]] as const;
const ORDER = ["pending", "in_review", "finalized"] as const;
const WORD: Readonly<Record<string, string>> = {
  pending: COMPENSATION_REVIEW_STATUS.pending, in_review: COMPENSATION_REVIEW_STATUS.inReview, finalized: COMPENSATION_REVIEW_STATUS.finalized,
};

export function CompReview({ requestId }: { requestId: string }) {
  const { register } = useViewState();
  useEffect(() => register({ view: "compensation-review", ids: { request: requestId }, summary: `compensation review ${requestId}` }), [register, requestId]);
  return (
    <ReviewBody requestId={requestId} />
  );
}

/** The worksheet's figures as the form holds them (DC-230): strings for inputs, "" for an unset choice. */
export interface Figures {
  salary_grade: string;
  market_mid: string;
  recommended_range_min: string;
  recommended_range_max: string;
  positioning_level: "" | "below_mid" | "at_mid" | "above_mid";
  positioning: string;
  bonus_target_pct: string;
  has_commission: boolean;
  commission_structure: string;
  working_notes: string;
  approval_evidence: string;
}

export const FIGURES: readonly (keyof Figures)[] = [
  "salary_grade", "market_mid", "recommended_range_min", "recommended_range_max", "positioning_level", "positioning", "bonus_target_pct",
  "has_commission", "commission_structure", "working_notes", "approval_evidence",
];

/** The worksheet's text fields and their server limits (the generated manifest, `api/inputLimits.ts`). */
export const TEXT_FIELDS = ["positioning", "commission_structure", "working_notes", "approval_evidence"] as const;
export type Limits = Partial<Record<(typeof TEXT_FIELDS)[number], number>>;

/** Bonus target % (C2: ge=0, le=100) and a money figure (≥ 0, the server's 10 integer digits). */
const BONUS = { min: 0, max: 100 } as const;
const MONEY_MAX = 9_999_999_999;

const POSITIONING_LEVELS = ["below_mid", "at_mid", "above_mid"] as const;

function marketMid(data: Record<string, unknown> | null | undefined): string {
  const mid = data?.["market_mid"];
  return mid === undefined || mid === null ? "" : String(mid);
}

/** The saved figures of a version (the base the worksheet's changes are measured against). */
export function figuresOf(version: VersionOut | null | undefined): Figures {
  const level = version?.positioning_level ?? "";
  return {
    salary_grade: version?.salary_grade ?? "",
    market_mid: marketMid(version?.market_data),
    recommended_range_min: version?.recommended_range_min ?? "",
    recommended_range_max: version?.recommended_range_max ?? "",
    positioning_level: (POSITIONING_LEVELS as readonly string[]).includes(level) ? level as Figures["positioning_level"] : "",
    positioning: version?.positioning ?? "",
    bonus_target_pct: version?.bonus_target_pct ?? "",
    has_commission: version?.has_commission ?? !!version?.commission_structure,
    commission_structure: version?.commission_structure ?? "",
    working_notes: version?.working_notes ?? "",
    approval_evidence: version?.approval_evidence ?? "",
  };
}

/** The worksheet a review opens on: the saved figures, the grade of the request's level preselected when none is saved. */
export function startOf(version: VersionOut | null | undefined, levelGrade: string | null | undefined): Figures {
  const figures = figuresOf(version);
  return figures.salary_grade ? figures : { ...figures, salary_grade: levelGrade ?? "" };
}

/** A money or percentage text as a number ("95,000" → 95000), or null when it is not one. */
export function numberOf(text: string): number | null {
  const clean = text.replace(/,/g, "").trim();
  if (!/^\d+(\.\d{1,2})?$/.test(clean)) return null;
  return Number(clean);
}

const same = (a: Figures, b: Figures, key: keyof Figures): boolean => {
  if (key === "commission_structure") return (a.has_commission ? a.commission_structure.trim() : "") === (b.has_commission ? b.commission_structure.trim() : "");
  if (key === "has_commission") return a.has_commission === b.has_commission;
  if (key === "market_mid" || key === "recommended_range_min" || key === "recommended_range_max" || key === "bonus_target_pct") {
    const x = numberOf(a[key]);
    const y = numberOf(b[key]);
    return x !== null && y !== null ? x === y : a[key].trim() === b[key].trim();
  }
  return String(a[key]).trim() === String(b[key]).trim();
};

/** The figures the worksheet changed against the saved version. */
export function changedFigures(form: Figures, base: Figures): (keyof Figures)[] {
  return FIGURES.filter((key) => !same(form, base, key));
}

/** The save the worksheet's changes make (C2's input: no recommendation, currency or internal range), or null when nothing
 * changed. An emptied figure is sent as null; a commission switched off clears its structure. */
export function saveInput(form: Figures, base: Figures, requestId: string, expectedVersion: number): CompReviewSaveInput | null {
  const changed = changedFigures(form, base);
  if (changed.length === 0) return null;
  const input: CompReviewSaveInput = { request_id: requestId, expected_version: expectedVersion };
  const text = (value: string) => (value.trim() === "" ? null : value.trim());
  const money = (value: string) => (value.trim() === "" ? null : value.replace(/,/g, "").trim());
  for (const key of changed) {
    switch (key) {
      case "salary_grade": input.salary_grade = text(form.salary_grade); break;
      case "market_mid": input.market_data = form.market_mid.trim() === "" ? null : { market_mid: numberOf(form.market_mid) ?? form.market_mid.trim() }; break;
      case "recommended_range_min": input.recommended_range_min = money(form.recommended_range_min); break;
      case "recommended_range_max": input.recommended_range_max = money(form.recommended_range_max); break;
      case "positioning_level": input.positioning_level = form.positioning_level || null; break;
      case "positioning": input.positioning = text(form.positioning); break;
      case "bonus_target_pct": input.bonus_target_pct = money(form.bonus_target_pct); break;
      case "has_commission": input.has_commission = form.has_commission; break;
      case "commission_structure": input.commission_structure = form.has_commission ? text(form.commission_structure) : null; break;
      case "working_notes": input.working_notes = text(form.working_notes); break;
      case "approval_evidence": input.approval_evidence = text(form.approval_evidence); break;
    }
  }
  return input;
}

/** What finalizing needs (C2's REQUIRED_TO_FINALIZE): the grade, the recommended range and the positioning. */
export const FINALIZE_NEEDS: readonly (keyof Figures)[] = ["salary_grade", "recommended_range_min", "recommended_range_max", "positioning_level"];

type Misses = Readonly<Record<string, string | undefined>>;

const filled = (form: Figures, key: keyof Figures): boolean => (typeof form[key] === "boolean" ? true : String(form[key]).trim() !== "");

/** The worksheet's own checks, before anything is sent: every server limit (a text over its limit, a number outside its
 * bounds, a Max below the Min) and, for Finalize, an empty required field — each on its field, in his words. */
export function worksheetMisses(form: Figures, limits: Limits, finalizing: boolean): Misses {
  const out: Record<string, string> = {};
  for (const key of TEXT_FIELDS) {
    const limit = limits[key];
    if (key === "commission_structure" && !form.has_commission) continue;
    if (limit !== undefined && form[key].length > limit) out[key] = atMostCharacters({ max: limit.toLocaleString("en-US") });
  }
  const between = (key: "market_mid" | "recommended_range_min" | "recommended_range_max" | "bonus_target_pct", min: number, max: number) => {
    if (form[key].trim() === "") return;
    const value = numberOf(form[key]);
    if (value === null || value < min || value > max) out[key] = betweenBounds({ min: min.toLocaleString("en-US"), max: max.toLocaleString("en-US") });
  };
  between("market_mid", 0, MONEY_MAX);
  between("recommended_range_min", 0, MONEY_MAX);
  between("recommended_range_max", 0, MONEY_MAX);
  between("bonus_target_pct", BONUS.min, BONUS.max);
  const low = numberOf(form.recommended_range_min);
  const high = numberOf(form.recommended_range_max);
  if (low !== null && high !== null && high < low && !out["recommended_range_max"]) out["recommended_range_max"] = HIRING_RANGE_LABELS.maxBelowMin;
  if (finalizing) for (const key of FINALIZE_NEEDS) if (!filled(form, key)) out[key] = COMMON_LABELS.required;
  return out;
}

/** Finalize (UAT F-26, F-155): a miss the form can see is shown on its field and nothing is sent; a complete worksheet
 * is sent (the caller saves the changed figures first, then finalizes, in the same click). */
export function finalizeWith(form: Figures, send: () => void, show: (misses: Misses) => void, limits: Limits = {}): void {
  const misses = worksheetMisses(form, limits, true);
  show(misses);
  if (Object.keys(misses).length === 0) send();
}

/** A server field key → the worksheet field it names (the market reference is sent as `market_data`). */
export function fieldOf(key: string): keyof Figures | null {
  if (key === "market_data") return "market_mid";
  return (FIGURES as readonly string[]).includes(key) ? key as keyof Figures : null;
}

/** A refusal that names a worksheet field: the field shows it, so the card draws no refusal banner of its own. */
export function formMiss(error: { fieldErrors: Readonly<Record<string, string>> } | null | undefined): boolean {
  return !!error && Object.keys(error.fieldErrors).some((key) => fieldOf(key) !== null);
}

/** A server refusal on a field in his words (DC-230 — never the validator's own English): an empty required field
 * reads Required, a filled one the server has not seen reads Not saved yet, a text over its limit At most {max}
 * characters, a number outside its bounds Between {min} and {max}, a Max below the Min Max is below Min. */
export function serverWords(fieldErrors: Readonly<Record<string, string>>, form: Figures, limits: Limits): Misses {
  const out: Record<string, string> = {};
  for (const [key, message] of Object.entries(fieldErrors)) {
    const field = fieldOf(key);
    if (!field) continue;
    const text = String(message ?? "");
    if (/required/i.test(text)) {
      out[field] = filled(form, field) ? HIRING_RANGE_LABELS.notSavedYet : COMMON_LABELS.required;
    } else if (field === "recommended_range_min" || field === "recommended_range_max") {
      out["recommended_range_max"] = /above|below/i.test(text) ? HIRING_RANGE_LABELS.maxBelowMin : betweenBounds({ min: "0", max: MONEY_MAX.toLocaleString("en-US") });
    } else if ((TEXT_FIELDS as readonly string[]).includes(field)) {
      const limit = limits[field as (typeof TEXT_FIELDS)[number]] ?? Number(/(\d+)/.exec(text)?.[1] ?? NaN);
      out[field] = Number.isFinite(limit) ? atMostCharacters({ max: limit.toLocaleString("en-US") }) : REQ_RULES.compensationAlone;
    } else if (field === "bonus_target_pct") {
      out[field] = betweenBounds({ min: BONUS.min, max: BONUS.max });
    } else if (field === "market_mid") {
      out[field] = betweenBounds({ min: "0", max: MONEY_MAX.toLocaleString("en-US") });
    } else {
      out[field] = REQ_RULES.compensationAlone;
    }
  }
  return out;
}

/** The band check under the range row (DC-230): the recommended range against the grade's internal range. It advises;
 * it never blocks Save or Finalize. */
export function bandCheck(form: Figures, grade: GradeOption | undefined): { word: string; within: boolean } | null {
  const low = numberOf(form.recommended_range_min);
  const high = numberOf(form.recommended_range_max);
  if (!grade || low === null || high === null || high < low) return null;
  const min = Number(grade.min);
  const max = Number(grade.max);
  if (low < min) return { word: HIRING_RANGE_LABELS.belowInternal, within: false };
  if (high > max) return { word: HIRING_RANGE_LABELS.aboveInternal, within: false };
  return { word: HIRING_RANGE_LABELS.withinInternal, within: true };
}

const whole = (value: string | number): string => {
  const n = Number(value);
  return Number.isFinite(n) ? n.toLocaleString("en-US", { maximumFractionDigits: 0 }) : String(value);
};

/** The grade's internal range as the read-only line *{min} – {max} {currency} · mid {mid}*. */
export function gradeLine(grade: GradeOption): string {
  return internalRangeLine({ min: whole(grade.min), max: whole(grade.max), currency: grade.currency, mid: whole(grade.mid) });
}

/** The counter under a text field from 80 % of its limit (*{n} / {max}*). */
export function counterOf(value: string, limit: number | undefined): string | undefined {
  if (limit === undefined || value.length < limit * 0.8) return undefined;
  return lengthCounter({ n: value.length.toLocaleString("en-US"), max: limit.toLocaleString("en-US") });
}

/** One Save or one Finalize click (F-155): the changed figures are saved first (when there are any), and Finalize then
 * finalizes with the row_version that save returned — never the stale one the worksheet loaded with. A refused save
 * stops the click there (the refusal is drawn); nothing is finalized on figures the server has not seen. */
export async function saveThenFinalize(
  kind: "save" | "finalize",
  input: CompReviewSaveInput | null,
  rowVersion: number,
  writes: {
    save: (input: CompReviewSaveInput) => Promise<{ row_version: number }>;
    finalize: (input: { request_id: string; expected_version: number }) => Promise<unknown>;
  },
  requestId: string,
): Promise<void> {
  let version = rowVersion;
  if (input) version = (await writes.save({ ...input, expected_version: rowVersion })).row_version;
  if (kind === "finalize") await writes.finalize({ request_id: requestId, expected_version: version });
}

/** The two writes of the worksheet, held above it so a refusal survives the worksheet's reload after a save: Save sends
 * the changed figures; Finalize saves them first and finalizes with the version that save returned, in one click. */
function useWorksheetWrites(requestId: string) {
  const save = useWrite((input: CompReviewSaveInput, key) => compReviewSave(input, key), READS(requestId));
  const finalize = useWrite((input: { request_id: string; expected_version: number }, key) => compReviewFinalize(input, key), READS(requestId));
  const [running, setRunning] = useState<"save" | "finalize" | null>(null);
  const run = async (kind: "save" | "finalize", input: CompReviewSaveInput | null, rowVersion: number) => {
    save.reset();
    finalize.reset();
    setRunning(kind);
    try {
      await saveThenFinalize(kind, input, rowVersion, { save: save.mutateAsync, finalize: finalize.mutateAsync }, requestId);
    } catch {
      // the refusal is the write's error, drawn on its field or as the card's banner
    } finally {
      setRunning(null);
    }
  };
  const error = save.error ?? finalize.error ?? null;
  return { run, running, error, reset: () => { save.reset(); finalize.reset(); } };
}

type Writes = ReturnType<typeof useWorksheetWrites>;

function ReviewBody({ requestId }: { requestId: string }) {
  const { me, can } = useSession();
  const timeZone = me?.timezone || DEFAULT_TIME_ZONE;
  // the read holds the capture settle signal until it arrives (CAPTURE.md H4)
  const q = useSettledReview(requestId);
  const writes = useWorksheetWrites(requestId);
  if (q.error && (q.error.status === 404 || q.error.code === "NOT_FOUND")) return <NotFound route={`compensation/${requestId}`} />;
  if (q.error) {
    return (
      <Stack gap={16}>
        <PageHeader title={REQ_LABELS.compensationReview} meta={[requestId]} />
        {isClientRefusal(q.error)
          ? <Banner tone="red" live>{q.error.remedy || q.error.message}</Banner>
          : <ErrorStrip message={q.error.remedy || RULE_LABELS.failedSyncRetryable} problem={{ status: q.error.status, refusal_code: q.error.code, detail: q.error.message, request_id: q.error.requestId }} onRetry={() => void q.refetch()}  />}
      </Stack>
    );
  }
  if (!q.data) {
    return (
      <Stack gap={16}>
        <PageHeader title={REQ_LABELS.compensationReview} meta={[requestId]} loading />
        <Split main={<Skeleton lines={8} />} side={<Skeleton lines={4} />} />
      </Stack>
    );
  }
  const review = q.data;
  const versions = [...(review.versions ?? [])].sort((a, b) => b.version_no - a.version_no);
  const latest = versions[0] ?? review.finalized ?? null;
  const level = review.level in LEVEL_DISPLAY ? LEVEL_DISPLAY[review.level as Level] : review.level;
  const step = ORDER.indexOf(review.status as (typeof ORDER)[number]);
  const steps = ORDER.map((key, index) => ({
    label: WORD[key]!,
    state: (index < step || (key === "finalized" && review.status === "finalized") ? "done" : index === step ? "on" : "next") as RailState,
  }));
  // the request's proposed range, drawn only when the field policy sent it (UAT F-27)
  const proposed = "target_comp_min" in review || "target_comp_max" in review
    ? range(review.target_comp_min, review.target_comp_max, review.target_currency)
    : "—";
  const levelGrade = (review.grades ?? []).find((option) => option.key === review.level_grade);
  const context: KeyValueItem[] = [
    ...(proposed !== "—" ? [[TIMING_LABELS.targetCompRange, proposed] as KeyValueItem] : []),
    ...(levelGrade ? [[COMP_FIELD_LABELS.internalRange, gradeLine(levelGrade)] as KeyValueItem] : []),
  ];
  return (
    <Stack gap={16}>
      <PageHeader
        title={REQ_LABELS.compensationReview}
        pills={<Pill>{WORD[review.status] ?? review.status}</Pill>}
        meta={[
          ...(can("comp_review.queue") ? [<TextLink key="rq" href="#compensation" >{NAV_ITEM_LABELS.compensationQueue}</TextLink>] : []),
          review.request_id,
          ...(review.requisition_id ? [<TextLink key="r" href={`#requisitions/${review.requisition_id}`} >{review.requisition_id}</TextLink>] : []),
          ...(review.title ? [review.title] : []),
          ...(review.locations?.length ? [review.locations.join(", ")] : []),
          ...(review.department ? [review.department] : []),
          ...(review.hiring_manager_name ? [`${ROLE_LABELS.hmAbbr} ${review.hiring_manager_name}`] : []),
          level,
          ...(review.job_code ? [review.job_code] : []),
          ...(proposed !== "—" ? [proposed] : []),
        ]}
      />
      {/* the stepper wraps onto a second row on a phone, never clipping Finalized (UAT F-86) */}
      <Rail wrap label={REQ_LABELS.compensationReview} steps={steps} />
      <Split
        main={review.can.save
          ? (
            <Worksheet
              key={`${review.row_version}`}
              requestId={requestId}
              rowVersion={review.row_version}
              latest={latest}
              status={review.status}
              canFinalize={!!review.can.finalize}
              grades={review.grades ?? []}
              levelGrade={review.level_grade ?? null}
              writes={writes}
            />
          )
          : <ReadCard review={review} latest={latest} canStart={!!review.can.start} canReturn={!!review.can.request_change} requestId={requestId} rowVersion={review.row_version} />}
        side={(
          <Stack gap={16}>
            <Card tight>
              <Stack gap={8}>
                {/* the request context (DC-234): the HRBP's target range beside the internal range of the level's grade */}
                {context.length ? <KeyValue rows items={context} /> : null}
                <Paragraph sm>{COMP_RULES.task}</Paragraph>
                <Paragraph sm>{REQ_RULES.compensationAlone}</Paragraph>
              </Stack>
            </Card>
            {versions.length ? (
              <Card tight title={REQ_LABELS.compensationReview}>
                <Versions
                  label={REQ_LABELS.compensationReview}
                  timeZone={timeZone}
                  rows={versions.map((v) => ({ n: v.version_no, at: v.effective_at, actor: v.author_id === me?.person_id ? me.name : "—", actorKey: v.author_id, reason: v.reason ?? (v.state === "draft" ? REQUEST_STATUS.draft : WORD[v.state] ?? "—") }))}
                />
              </Card>
            ) : null}
          </Stack>
        )}
      />
    </Stack>
  );
}

function useSettledReview(requestId: string) {
  return useSettledRead(["comp-review", requestId], () => compReviewOpen({ request_id: requestId }));
}

/** Every seat but the reviewing Compensation: the status, and the finalized figures the field policy sends, in the
 * worksheet's sections (DC-230) — the Hiring Manager reads the recommendation line and the range, nothing else. */
function ReadCard({ review, latest, canStart, canReturn, requestId, rowVersion }: {
  review: { status: string; finalized?: VersionOut | null; grades?: GradeOption[] }; latest: VersionOut | null; canStart: boolean; canReturn: boolean;
  requestId: string; rowVersion: number;
}) {
  const start = useWrite((input: { request_id: string; expected_version: number }, key) => compReviewStart(input, key), READS(requestId));
  const shown = review.status === "finalized" ? review.finalized ?? latest : canStart ? latest : null;
  const sections = readSections(shown ?? null, review.grades ?? []);
  return (
    <Stack gap={16}>
      <Card
        title={HIRING_RANGE_LABELS.hiringRange}
        pills={<Pill>{WORD[review.status] ?? review.status}</Pill>}
        actions={canStart ? <Button tone="primary" sm busy={start.isPending} onClick={() => void start.mutateAsync({ request_id: requestId, expected_version: rowVersion }).catch(() => undefined)} >{COMP_ACTION_LABELS.startReview}</Button> : undefined}
        refused={start.error ? { reason: start.error.remedy || start.error.message } : undefined}
        
      >
        {sections.length
          ? (
            <Stack gap={20}>
              {sections.map((section, index) => (
                <Section key={section.title} title={section.title} first={index === 0}>
                  <KeyValue rows items={section.items} />
                </Section>
              ))}
            </Stack>
          )
          : <Paragraph sm>{review.status === "pending" && canStart ? COMP_RULES.startToEnter : WORD[review.status] ?? review.status}</Paragraph>}
      </Card>
      {canReturn ? <ReturnCard requestId={requestId} /> : null}
    </Stack>
  );
}

/** The Recruiter's or the HR Business Partner's *Return* of a finalized review, with the reason. */
function ReturnCard({ requestId }: { requestId: string }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const ask = useWrite((input: { request_id: string; reason: string }, key) => compReviewRequestChange(input, key), READS(requestId));
  const fields = ask.error?.fieldErrors ?? {};
  return (
    <>
      <Flex gap={8}><Button tone="ghost" onClick={() => setOpen(true)} >{ACTION_LABELS.return}</Button></Flex>
      <Dialog
        title={ACTION_LABELS.return}
        open={open}
        onClose={() => setOpen(false)}
        dismissLabel={DIALOG_LABELS.cancel}
        primary={{ label: ACTION_LABELS.return, busy: ask.isPending, onClick: () => void ask.mutateAsync({ request_id: requestId, reason: reason.trim() }).then(() => setOpen(false)).catch(() => undefined), hs: "comp.return" }}
        refused={ask.error && !fields.reason ? { reason: ask.error.remedy || ask.error.message } : undefined}
        
      >
        <Field label={G1_LABELS.reason} required error={fields.reason} hint={G1_LABELS.reasonStored}>
          <TextArea value={reason} onChange={(event) => setReason(event.target.value)}  />
        </Field>
      </Dialog>
    </>
  );
}

/** A section of the Hiring range card (DC-230 level 1): a heading (h4) over its fields, a hairline above all but the first. */
function Section({ title, first, children }: { title: string; first?: boolean; children: ReactNode }) {
  return (
    <Stack gap={12}>
      {first ? null : <Divider />}
      <h4 className="mc-p mc-p-strong"><b>{title}</b></h4>
      <Stack gap={14}>{children}</Stack>
    </Stack>
  );
}

// Level 1 is drawn with the design system's own classes (a view carries no V2 token of its own): the strong paragraph
// face (13.5 px, --text) in bold, a size and weight above the 12 px / 600 --text-2 field labels.

/** Compensation's worksheet while the review is In Review (DC-230): the one card *Hiring range* in five sections. Every
 * limit the server enforces is checked on screen first; a refusal is named on its field, a field-less one is the card's
 * banner; Finalize saves the changed figures and finalizes them in the same click. */
function Worksheet({ requestId, rowVersion, latest, status, canFinalize, grades, levelGrade, writes }: {
  requestId: string; rowVersion: number; latest: VersionOut | null; status: string; canFinalize: boolean;
  grades: readonly GradeOption[]; levelGrade: string | null; writes: Writes;
}) {
  const { manifest } = useSession();
  const ids = useId();
  const limits: Limits = Object.fromEntries(TEXT_FIELDS.map((key) => [key, inputMaxLength(manifest, "comp_review.save", key)]).filter(([, limit]) => limit !== undefined));
  const base = figuresOf(latest);
  const [form, setForm] = useState<Figures>(() => startOf(latest, levelGrade));
  const [misses, setMisses] = useState<Misses>({});
  // the fields edited since the last Save or Finalize: an edit clears its own field's refusal and the card's banner
  const [edited, setEdited] = useState<ReadonlySet<string>>(() => new Set());
  const [lastKind, setLastKind] = useState<"save" | "finalize">("save");
  const set = <K extends keyof Figures>(key: K, value: Figures[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setMisses((prev) => (prev[key] === undefined ? prev : { ...prev, [key]: undefined }));
    setEdited((prev) => (prev.has(key) ? prev : new Set(prev).add(key)));
  };
  const text = (key: keyof Figures) => (event: { target: { value: string } }) => set(key, event.target.value as never);
  const error = writes.error;
  const server = error ? serverWords(error.fieldErrors, form, limits) : {};
  const live = worksheetMisses(form, limits, false);
  const errors: Misses = {
    ...Object.fromEntries(Object.entries(server).filter(([key]) => !edited.has(key))),
    ...live,
    ...Object.fromEntries(Object.entries(misses).filter(([, word]) => word !== undefined)),
  };
  // the card's banner: a refusal the form cannot show on a field, until the next edit
  const refusal = error && !formMiss(error) && edited.size === 0 ? error : null;
  const liveReason = Object.values(live)[0];
  const grade = grades.find((option) => option.key === form.salary_grade);
  const currency = grade?.currency ?? latest?.currency ?? "";
  const band = bandCheck(form, grade);
  const attempt = (kind: "save" | "finalize") => {
    setLastKind(kind);
    setEdited(new Set());
    const found = worksheetMisses(form, limits, kind === "finalize");
    setMisses(found);
    if (Object.keys(found).length > 0) {
      const first = FIGURES.find((key) => found[key] !== undefined);
      if (first && typeof document !== "undefined") document.getElementById(`${ids}-${first}`)?.focus();
      return;
    }
    void writes.run(kind, saveInput(form, base, requestId, rowVersion), rowVersion);
  };
  const busy = writes.running !== null;
  const nothingToSave = changedFigures(form, base).length === 0;
  const textField = (key: (typeof TEXT_FIELDS)[number], label: string, long: boolean, hs: string) => (
    <Field id={`${ids}-${key}`} label={label} error={errors[key]} hint={counterOf(form[key], limits[key])}>
      {long
        ? <TextArea value={form[key]} onChange={text(key)} rows={4} hs={hs} />
        : <Input value={form[key]} onChange={text(key)} hs={hs} />}
    </Field>
  );
  return (
    <Card
      title={HIRING_RANGE_LABELS.hiringRange}
      pills={<Pill>{WORD[status] ?? status}</Pill>}
      refused={refusal && isClientRefusal(refusal) ? { reason: refusal.remedy || refusal.message } : undefined}
      error={refusal && !isClientRefusal(refusal)
        ? { message: refusal.remedy || RULE_LABELS.failedSyncRetryable, problem: { status: refusal.status, refusal_code: refusal.code, detail: refusal.message, request_id: refusal.requestId }, onRetry: () => attempt(lastKind), hs: "comp.retry-write" }
        : undefined}
      
    >
      <div {...(busy ? { inert: "" } : {})} aria-busy={busy || undefined}>
        <Stack gap={20}>
          <Section title={HIRING_RANGE_LABELS.grade} first>
            <Grid cols={2} min={320}>
              <Field id={`${ids}-salary_grade`} label={COMP_ROW_LABELS.salaryGrade} required error={errors["salary_grade"]}>
                <Select
                  aria-label={COMP_ROW_LABELS.salaryGrade}
                  value={form.salary_grade}
                  placeholder="—"
                  options={grades.map((option) => ({ value: option.key, label: option.label }))}
                  onChange={(value) => set("salary_grade", value)}
                  
                />
              </Field>
              <Field label={COMP_FIELD_LABELS.internalRange} mode="view">
                <span className="mc-field-v mc-mono">{grade ? gradeLine(grade) : "—"}</span>
              </Field>
            </Grid>
          </Section>
          <Section title={HIRING_RANGE_LABELS.marketData}>
            {latest?.market_data && !("market_mid" in latest.market_data)
              ? <Field label={COMP_FIELD_LABELS.marketData} mode="view"><MarketData data={latest.market_data} /></Field>
              : null}
            <Field id={`${ids}-market_mid`} label={HIRING_RANGE_LABELS.marketMid} error={errors["market_mid"]}>
              <Flex gap={8} align="center">
                <Input value={form.market_mid} onChange={text("market_mid")} inputMode="decimal" mono  />
                {currency ? <span className="mc-hint">{currency}</span> : null}
              </Flex>
            </Field>
          </Section>
          <Section title={HIRING_RANGE_LABELS.recommendation}>
            <RangeRow
              id={`${ids}-recommended_range_min`}
              maxId={`${ids}-recommended_range_max`}
              form={form}
              currency={currency}
              errors={errors}
              band={band}
              onMin={text("recommended_range_min")}
              onMax={text("recommended_range_max")}
            />
            <Field id={`${ids}-positioning_level`} label={COMP_FIELD_LABELS.positioning} required error={errors["positioning_level"]}>
              <Segmented
                label={COMP_FIELD_LABELS.positioning}
                value={form.positioning_level}
                options={POSITIONING_LEVELS.map((value) => ({ value, label: POSITIONING_WORD[value]!, hs: `comp.f.positioning.${value.replace(/_/g, "-")}` }))}
                onChange={(value) => set("positioning_level", value as Figures["positioning_level"])}
              />
            </Field>
            {textField("positioning", HIRING_RANGE_LABELS.positioningNote, false, "comp.f.positioning-note")}
          </Section>
          <Section title={HIRING_RANGE_LABELS.bonusCommission}>
            <Grid cols={2} min={320}>
              <Field id={`${ids}-bonus_target_pct`} label={HIRING_RANGE_LABELS.bonusTargetPct} error={errors["bonus_target_pct"]}>
                <Flex gap={8} align="center">
                  <Input value={form.bonus_target_pct} onChange={text("bonus_target_pct")} inputMode="decimal" mono  />
                  <span className="mc-hint">%</span>
                </Flex>
              </Field>
              <Toggle
                label={HIRING_RANGE_LABELS.commission}
                checked={form.has_commission}
                onChange={(on) => set("has_commission", on)}
                sub={form.has_commission ? undefined : HIRING_RANGE_LABELS.none}
                
              />
            </Grid>
            {form.has_commission ? textField("commission_structure", COMP_FIELD_LABELS.commissionStructure, true, "comp.f.commission-structure") : null}
          </Section>
          <Section title={HIRING_RANGE_LABELS.notesEvidence}>
            {textField("working_notes", COMP_FIELD_LABELS.workingNotes, true, "comp.f.working-notes")}
            {textField("approval_evidence", COMP_FIELD_LABELS.approvalEvidence, false, "comp.f.approval-evidence")}
          </Section>
          <Paragraph sm>{COMP_RULES.restricted}</Paragraph>
          <Stack gap={6}>
            <Flex gap={8} wrap>
              <Button
                tone="ghost"
                busy={writes.running === "save"}
                blocked={liveReason ? { reason: liveReason } : undefined}
                onClick={() => { if (!nothingToSave) attempt("save"); }}
                
              >
                {ACTION_LABELS.save}
              </Button>
              {canFinalize ? (
                <Button
                  tone="primary"
                  busy={writes.running === "finalize"}
                  blocked={liveReason ? { reason: liveReason } : undefined}
                  onClick={() => attempt("finalize")}
                  
                >
                  {ACTION_LABELS.finalize}
                </Button>
              ) : null}
            </Flex>
            {canFinalize ? <p className="mc-hint">{HIRING_RANGE_LABELS.finalizeFreezes}</p> : null}
          </Stack>
        </Stack>
      </div>
    </Card>
  );
}

const POSITIONING_WORD: Readonly<Record<string, string>> = {
  below_mid: HIRING_RANGE_LABELS.belowMid, at_mid: HIRING_RANGE_LABELS.atMid, above_mid: HIRING_RANGE_LABELS.aboveMid,
};

/** The recommended hiring range as ONE labelled row (DC-230): Min – Max, the grade's currency as plain text after it, the
 * band check under it. Each input's accessible name is the field label and its in-field part (Min, Max); only the empty
 * or wrong input is marked. */
function RangeRow({ id, maxId, form, currency, errors, band, onMin, onMax }: {
  id: string; maxId: string; form: Figures; currency: string; errors: Misses; band: { word: string; within: boolean } | null;
  onMin: (event: { target: { value: string } }) => void; onMax: (event: { target: { value: string } }) => void;
}) {
  const minError = errors["recommended_range_min"];
  const maxError = errors["recommended_range_max"];
  // each input's accessible name is the field label and its part (Min, Max); the Required marker is its aria-required
  const label = HIRING_RANGE_LABELS.recommendedHiringRange;
  return (
    <Field
      id={id}
      label={label}
      required
      error={minError ?? maxError}
      hint={band ? band.word : undefined}
      hintTone={band && !band.within ? "flag" : undefined}
    >
      <Flex gap={8} align="center" wrap>
        <span className="mc-hint" aria-hidden="true">{COMP_FIELD_LABELS.min}</span>
        <Input id={id} value={form.recommended_range_min} onChange={onMin} inputMode="decimal" mono invalid={!!minError} aria-label={`${label} ${COMP_FIELD_LABELS.min}`}  />
        <span aria-hidden="true">–</span>
        <span className="mc-hint" aria-hidden="true">{COMP_FIELD_LABELS.max}</span>
        <Input id={maxId} value={form.recommended_range_max} onChange={onMax} inputMode="decimal" mono invalid={!!maxError} aria-label={`${label} ${COMP_FIELD_LABELS.max}`}  />
        {currency ? <span className="mc-hint">{currency}</span> : null}
      </Flex>
    </Field>
  );
}
